import React, { useState, useRef, useCallback, useEffect } from 'react';

const CompactBeforeAfterSlider = ({ 
  beforeSrc, 
  afterSrc, 
  altBefore = "Before restoration",
  altAfter = "After restoration",
  initialPercent = 50,
  aspectRatio = "4/3" // Default 4:3, can be "16/9", "1/1", "3/4" for portraits
}) => {
  const [sliderPosition, setSliderPosition] = useState(initialPercent);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(0, prev - 2));
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(100, prev + 2));
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-xl shadow-lg select-none cursor-ew-resize"
      style={{
        aspectRatio: aspectRatio,
        border: '1px solid rgba(201, 169, 98, 0.15)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="slider"
      aria-valuenow={Math.round(sliderPosition)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Before and after image comparison slider"
      data-testid="compact-before-after-slider"
    >
      {/* BEFORE Image (Base Layer - fully visible, shown on LEFT) */}
      <img 
        src={beforeSrc}
        alt={altBefore}
        className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
        draggable={false}
      />

      {/* AFTER Image (Clipped Layer - revealed from RIGHT side) */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ 
          clipPath: `inset(0 0 0 ${sliderPosition}%)` 
        }}
      >
        <img 
          src={afterSrc}
          alt={altAfter}
          className="absolute inset-0 w-full h-full object-cover object-center"
          draggable={false}
        />
      </div>

      {/* Slider Handle - Compact version */}
      <div 
        className="absolute top-0 bottom-0 z-20 pointer-events-none"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Thin Vertical Line */}
        <div 
          className="absolute top-0 bottom-0 w-[1.5px]"
          style={{
            background: 'linear-gradient(to bottom, rgba(201, 169, 98, 0.2), rgba(201, 169, 98, 0.9), rgba(201, 169, 98, 0.2))',
            boxShadow: '0 0 6px rgba(201, 169, 98, 0.3)'
          }}
        />
        
        {/* Small Center Knob */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #C9A962 0%, #A8894D 100%)',
            boxShadow: '0 0 12px rgba(201, 169, 98, 0.4), inset 0 1px 2px rgba(255,255,255,0.2)'
          }}
        >
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #0f1724 0%, #1a2332 100%)',
              border: '1.5px solid rgba(201, 169, 98, 0.5)'
            }}
          >
            {/* Small Arrows */}
            <div className="flex items-center gap-0.5">
              <svg className="w-2 h-2 text-[#C9A962]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              <svg className="w-2 h-2 text-[#C9A962]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* BEFORE Label (Left side) */}
      <div className="absolute bottom-2 left-2 z-10 pointer-events-none">
        <span 
          className="px-2 py-1 text-[8px] font-medium tracking-[0.15em] uppercase rounded"
          style={{
            background: 'rgba(15, 23, 36, 0.7)',
            backdropFilter: 'blur(4px)',
            color: 'rgba(201, 169, 98, 0.8)',
            border: '1px solid rgba(201, 169, 98, 0.15)'
          }}
        >
          Before
        </span>
      </div>

      {/* AFTER Label (Right side) */}
      <div className="absolute bottom-2 right-2 z-10 pointer-events-none">
        <span 
          className="px-2 py-1 text-[8px] font-medium tracking-[0.15em] uppercase rounded"
          style={{
            background: 'rgba(201, 169, 98, 0.8)',
            color: '#0f1724',
            fontWeight: '600'
          }}
        >
          After
        </span>
      </div>

      {/* Subtle vignette */}
      <div 
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.2) 100%)'
        }}
      />
    </div>
  );
};

export default CompactBeforeAfterSlider;
